import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, RefreshCw, Home, Bug, Send } from 'lucide-react';
import { captureError } from '../utils/sentry';
import { useToast } from '../hooks/useToast';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  errorId?: string;
  retryCount: number;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
      errorId: Math.random().toString(36).substr(2, 9),
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);

    // Store error info for debugging
    this.setState({ errorInfo });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Send to error monitoring service
    captureError(error, {
      component: 'ErrorBoundary',
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
    });
  }

  private handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      retryCount: prevState.retryCount + 1,
    }));
  };

  private handleReportError = () => {
    if (this.state.error && this.state.errorId) {
      // In a real app, this would send to your error reporting service
      const errorReport = {
        errorId: this.state.errorId,
        message: this.state.error.message,
        stack: this.state.error.stack,
        componentStack: this.state.errorInfo?.componentStack,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      };

      console.log('Error report:', errorReport);

      // Show success toast
      const toast = useToast();
      toast.success('Error Reported', 'Thank you for helping us improve!');
    }
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md w-full bg-white rounded-3xl p-8 shadow-2xl text-center"
          >
            <div className="w-20 h-20 bg-gradient-to-r from-red-400 to-pink-400 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-10 h-10 text-white" />
            </div>

            <h1 className="text-3xl font-bold text-gray-800 mb-4">
              Oops! Something went wrong
            </h1>

            <p className="text-gray-600 mb-4">
              We're sorry, but something unexpected happened. Don't worry, it's
              not your fault!
            </p>

            {this.state.errorId && (
              <p className="text-sm text-gray-500 mb-6">
                Error ID:{' '}
                <code className="bg-gray-100 px-2 py-1 rounded">
                  {this.state.errorId}
                </code>
              </p>
            )}

            {/* Error details in development */}
            {import.meta.env.DEV && this.state.error && (
              <details className="text-left bg-gray-50 rounded-lg p-4 mb-6 text-sm">
                <summary className="cursor-pointer font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Bug size={16} />
                  Error Details (Development)
                </summary>
                <div className="text-red-600 font-mono text-xs">
                  <p className="font-semibold mb-2">
                    {this.state.error.name}: {this.state.error.message}
                  </p>
                  {this.state.error.stack && (
                    <pre className="whitespace-pre-wrap overflow-auto max-h-32 bg-red-50 p-2 rounded">
                      {this.state.error.stack}
                    </pre>
                  )}
                  {this.state.errorInfo?.componentStack && (
                    <div className="mt-2">
                      <p className="font-semibold">Component Stack:</p>
                      <pre className="whitespace-pre-wrap overflow-auto max-h-32 bg-red-50 p-2 rounded">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}

            <div className="flex flex-col gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={this.handleRetry}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
              >
                <RefreshCw size={20} />
                Try Again
              </motion.button>

              <div className="grid grid-cols-3 gap-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={this.handleGoHome}
                  className="bg-gray-200 text-gray-700 px-3 py-2 rounded-full font-medium hover:bg-gray-300 transition-colors duration-300 flex items-center justify-center gap-1 text-xs"
                >
                  <Home size={14} />
                  Home
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={this.handleRefresh}
                  className="bg-gray-200 text-gray-700 px-3 py-2 rounded-full font-medium hover:bg-gray-300 transition-colors duration-300 flex items-center justify-center gap-1 text-xs"
                >
                  <RefreshCw size={14} />
                  Refresh
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={this.handleReportError}
                  className="bg-blue-200 text-blue-700 px-3 py-2 rounded-full font-medium hover:bg-blue-300 transition-colors duration-300 flex items-center justify-center gap-1 text-xs"
                >
                  <Send size={14} />
                  Report
                </motion.button>
              </div>
            </div>

            <p className="text-xs text-gray-500 mt-6">
              If this problem persists, please contact our support team.
            </p>
          </motion.div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
